import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Undo2, HeartHandshake, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const REQUIRED_PHRASE = 'RESTORE';

/**
 * Restore Wiped Real Players — bulk goodwill credit tool.
 *
 * Targets the ~200 real customer accounts caught by the old (broken)
 * Wipe Test Players heuristic in June 2026 — those users have `wiped_at`
 * stamped and their transactions were deleted, so original balances can't
 * be reconstructed. Solution: send each one a flat goodwill credit,
 * stamp `restored_at` so we never re-credit the same player twice, and
 * fire a player notification so they see the gift land in their wallet.
 */
export const RestoreWipedPlayersCard = () => {
    const [open, setOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const [amount, setAmount] = useState('25');
    const [note, setNote] = useState('');
    const [phrase, setPhrase] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const loadPreview = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/wiped-players/preview`,
                { withCredentials: true });
            setPreview(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load preview');
        }
    };

    const openModal = async () => {
        setOpen(true); setResult(null); setPhrase(''); setPreview(null);
        setAmount('25'); setNote('');
        await loadPreview();
    };

    const close = (o) => {
        if (loading) return;
        setOpen(o);
        if (!o) { setPhrase(''); setPreview(null); setResult(null); }
    };

    const parsedAmount = parseFloat(amount);
    const amountValid = !Number.isNaN(parsedAmount) && parsedAmount >= 0.01 && parsedAmount <= 500;
    const phraseValid = phrase.trim() === REQUIRED_PHRASE;

    const doRestore = async () => {
        if (!phraseValid) { toast.error(`Type exactly: ${REQUIRED_PHRASE}`); return; }
        if (!amountValid) { toast.error('Amount must be between $0.01 and $500'); return; }
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/admin/wiped-players/restore`, {
                confirm: 'RESTORE',
                amount_per_player: parsedAmount,
                note: note.trim() || undefined,
            }, { withCredentials: true });
            setResult(data);
            toast.success(`Restored ${data.restored_count} players · ${fmt(data.total_credited)} total`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Restore failed');
        }
        setLoading(false);
    };

    const totalCost = amountValid && preview ? parsedAmount * preview.count : 0;

    return (
        <div className="rounded-lg border border-[#39FF14]/30 bg-[#0a0a0a] p-4 mt-3" data-testid="restore-wiped-players-card">
            <div className="flex items-start gap-3 mb-3">
                <HeartHandshake className="w-5 h-5 text-[#39FF14] shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm">Restore Wiped Real Players</p>
                    <p className="text-[11px] text-[#A0AAB2] mt-1">
                        Bulk goodwill credit for real customers accidentally caught by the older Wipe Test Players heuristic (their <span className="font-mono">wiped_at</span> is stamped).
                        Skips accounts already restored (idempotent). Fires a wallet transaction + a <span className="font-mono">🎁 gift</span> notification per player.
                    </p>
                </div>
            </div>
            <Button
                onClick={openModal}
                className="bg-[#39FF14] hover:bg-[#39FF14]/90 text-black font-bold w-full sm:w-auto"
                data-testid="open-restore-wiped-btn"
            >
                <Undo2 className="w-4 h-4 mr-2" /> Restore wiped players…
            </Button>

            <Dialog open={open} onOpenChange={close}>
                <DialogContent className="bg-[#121212] border-[#39FF14]/40 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Gift className="w-5 h-5 text-[#39FF14]" />
                            {result ? 'Goodwill credit sent' : 'Restore Wiped Players'}
                        </DialogTitle>
                        {!result && (
                            <DialogDescription className="text-[#A0AAB2]">
                                Send each affected real customer a one-time goodwill credit. Amount is per-player.
                            </DialogDescription>
                        )}
                    </DialogHeader>

                    {result ? (
                        <div className="space-y-3 py-2" data-testid="restore-result-panel">
                            <div className="bg-[#0a0a0a] rounded-lg p-3 border border-[#39FF14]/30">
                                <p className="text-[#39FF14] font-bold mb-2">{result.message}</p>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-[#A0AAB2]">
                                    <div className="flex justify-between"><span>Players restored</span><span className="text-white font-mono">{result.restored_count}</span></div>
                                    <div className="flex justify-between"><span>Per player</span><span className="text-white font-mono">{fmt(result.amount_per_player)}</span></div>
                                    <div className="flex justify-between col-span-2"><span>Total credited</span><span className="text-[#39FF14] font-mono font-black">{fmt(result.total_credited)}</span></div>
                                </div>
                            </div>
                            <p className="text-[10px] text-[#666]">
                                Each restored player got a wallet credit and a 🎁 notification. They will not be restored again by this tool.
                            </p>
                            <DialogFooter>
                                <Button onClick={() => close(false)} className="bg-[#FFD700] text-black font-bold w-full" data-testid="restore-done-btn">Done</Button>
                            </DialogFooter>
                        </div>
                    ) : !preview ? (
                        <p className="text-[#A0AAB2] text-sm py-4 flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading affected players…
                        </p>
                    ) : preview.count === 0 ? (
                        <div className="space-y-3 py-4">
                            <p className="text-[#39FF14] text-sm font-bold text-center">✨ Nothing to restore.</p>
                            <p className="text-[#A0AAB2] text-[11px] text-center">
                                No real customer accounts are currently flagged as wiped-but-unrestored.
                            </p>
                            <DialogFooter>
                                <Button onClick={() => close(false)} className="bg-white/10 text-white w-full">Close</Button>
                            </DialogFooter>
                        </div>
                    ) : (
                        <div className="space-y-4 py-2">
                            <div className="rounded-lg border border-[#39FF14]/40 bg-[#39FF14]/5 p-3 space-y-1">
                                <p className="text-[12px] font-bold text-[#39FF14]">Affected accounts:</p>
                                <div className="grid grid-cols-2 gap-2 text-[12px]">
                                    <div className="flex justify-between"><span className="text-[#A0AAB2]">Real players wiped</span><span className="text-white font-mono font-black" data-testid="restore-preview-count">{preview.count}</span></div>
                                    <div className="flex justify-between"><span className="text-[#A0AAB2]">Est. total</span><span className={`font-mono font-black ${amountValid ? 'text-[#39FF14]' : 'text-[#A0AAB2]'}`} data-testid="restore-preview-total">{amountValid ? fmt(totalCost) : '—'}</span></div>
                                </div>
                            </div>

                            <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-2 max-h-40 overflow-y-auto" data-testid="restore-list">
                                <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold mb-1.5 px-1">First {Math.min(preview.players.length, 20)} of {preview.count}</p>
                                {preview.players.slice(0, 20).map((p) => (
                                    <div key={p.id} className="flex justify-between gap-2 text-[11px] px-1 py-0.5 text-zinc-200">
                                        <span className="truncate flex-1">
                                            {p.name || '(no name)'} · <span className="text-[#A0AAB2]">{p.phone || p.email || '—'}</span>
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="text-white text-xs">Per-player credit ($)</Label>
                                    <Input
                                        type="number" step="1" min="0.01" max="500"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        className="bg-[#0a0a0a] border-white/10 text-white font-mono mt-1"
                                        data-testid="restore-amount-input"
                                    />
                                    <p className="text-[10px] text-[#A0AAB2] mt-1">$0.01 – $500 per player</p>
                                </div>
                                <div>
                                    <Label className="text-white text-xs">Wallet history note (optional)</Label>
                                    <Input
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        placeholder="Goodwill restore — accidental wipe"
                                        maxLength={200}
                                        className="bg-[#0a0a0a] border-white/10 text-white mt-1"
                                        data-testid="restore-note-input"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label className="text-white text-sm">
                                    Type <span className="font-mono bg-[#0a0a0a] px-2 py-0.5 rounded text-[#39FF14]">{REQUIRED_PHRASE}</span> to confirm:
                                </Label>
                                <Input
                                    value={phrase}
                                    onChange={(e) => setPhrase(e.target.value)}
                                    placeholder={REQUIRED_PHRASE}
                                    className="bg-[#0a0a0a] border-white/10 text-white font-mono mt-2"
                                    autoFocus autoComplete="off"
                                    data-testid="restore-confirm-input"
                                />
                            </div>

                            <DialogFooter className="gap-2">
                                <Button variant="outline" onClick={() => close(false)} disabled={loading} className="border-white/20 text-white">Cancel</Button>
                                <Button
                                    onClick={doRestore}
                                    disabled={loading || !phraseValid || !amountValid || preview.count === 0}
                                    className="bg-[#39FF14] hover:bg-[#39FF14]/90 text-black font-bold disabled:opacity-50"
                                    data-testid="confirm-restore-btn"
                                >
                                    <Gift className="w-4 h-4 mr-2" />
                                    {loading ? 'Restoring…' : `Send ${fmt(parsedAmount || 0)} × ${preview.count}`}
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default RestoreWipedPlayersCard;
